import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalAgregarProductoSuscripcion from '../suscripciones/Modales/ModalAgregarProductoSuscripcion';

const SeccionModulosLicenciaCliente = ({ idLicencia, infoSuscripcion }) => {
    const { token } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const [modulos, setModulos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [moduloARemover, setModuloARemover] = useState(null);
    const [vMotivoRemocion, setVMotivoRemocion] = useState('');
    const [remiendo, setRemiendo] = useState(false);

    // Carga los módulos asociados a la licencia
    const cargarModulos = useCallback(async () => {
        if (!idLicencia) return;
        setCargando(true);
        try {
            const resp = await fetch(`/api/licencia/ObtenerModulosPorLicencia?idLicencia=${idLicencia}`, {
                method: 'GET',
                cache: 'no-store',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            const data = await resp.json();
            if (data.iResponseType === 1 && data.detalles) {
                const det = JSON.parse(data.detalles);
                setModulos(Array.isArray(det?.table2) ? det.table2 : []);
            } else {
                setModulos([]);
            }
        } catch {
            showErrorToast('Error al cargar módulos de la licencia');
        } finally {
            setCargando(false);
        }
    }, [idLicencia, token]);

    useEffect(() => {
        cargarModulos();
    }, [cargarModulos]);

    const getEstadoModulo = m => {
        if (typeof m?.bIsActive === 'boolean') return m.bIsActive;
        if (m?.bIsActive === 1 || m?.bIsActive === '1') return true;
        if (m?.bIsActive === 0 || m?.bIsActive === '0') return false;

        // Fallback para data histórica: fecha fin futura = activo
        if (!m?.dFechaFin) return false;
        const fechaFin = new Date(m.dFechaFin);
        return !Number.isNaN(fechaFin.getTime()) && fechaFin > new Date();
    };

    const formatearFechaAsignacion = fecha => {
        if (!fecha) return '—';
        const parsed = new Date(fecha);
        if (Number.isNaN(parsed.getTime())) return '—';
        return parsed.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleAbrirModalRemover = modulo => {
        setModuloARemover(modulo);
        setVMotivoRemocion('');
    };

    const handleConfirmarRemover = async () => {
        const idSuscripcion = moduloARemover?.idSuscripcion || infoSuscripcion?.idSuscripcion;
        if (!moduloARemover || !idSuscripcion) return;
        setRemiendo(true);
        try {
            const targetIdModulo = moduloARemover.idModulosLicenciaCliente ?? moduloARemover.idModuloLicencia ?? moduloARemover.idModulo ?? null;

            const payload = {
                idSuscripcion: idSuscripcion,
                idModuloLicencia: targetIdModulo,
                idModulosLicenciaCliente: targetIdModulo,
                idSuscripcionDetalle: moduloARemover.idSuscripcionDetalle || null,
                vMotivo: vMotivoRemocion.trim() || 'Removido desde AdminPanel'
            };

            const resp = await fetch('/api/licencia/RemoverModuloSuscripcion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await resp.json();
            if (data.iResponseType === 1) {
                showSuccessToast('Módulo removido de la suscripción exitosamente');
                setModuloARemover(null);
                setVMotivoRemocion('');
                cargarModulos();
            } else {
                showErrorToast(data.vMessage || 'No se pudo remover el módulo');
            }
        } catch {
            showErrorToast('Error al conectar con el servidor para remover el módulo');
        } finally {
            setRemiendo(false);
        }
    };

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <FontAwesomeIcon icon={icon({ name: 'puzzle-piece' })} className="me-1" />
                        Módulos Akasia POS </h5>

                    {infoSuscripcion && (
                        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
                            <FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} className="me-1" />
                            Agregar Módulo
                        </Button>
                    )}
                </Card.Header>

                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <>
                            {/* Vista Escritorio / Tablet (Tabla) */}
                            <div className="d-none d-md-block">
                                <Table responsive hover className="mb-0 small" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-dark fw-bold">#</th>
                                            <th className="text-dark fw-bold">Clave Módulo</th>
                                            <th className="text-dark fw-bold">Estado Módulo</th>
                                            <th className="text-dark fw-bold">Suscripción Vinculada</th>
                                            <th className="text-dark fw-bold">Fecha Inicio</th>
                                            <th className="text-dark fw-bold">Fecha Fin</th>
                                            <th className="text-dark fw-bold">Asignado por</th>
                                            <th className="text-dark fw-bold text-end">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modulos.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center text-muted py-4">
                                                    Sin módulos asignados.
                                                </td>
                                            </tr>
                                        ) : (
                                            modulos.map((m, idx) => {
                                                const isActive = getEstadoModulo(m);
                                                const idModulo = m.idModuloLicencia ?? m.idModulo ?? idx;
                                                const idSuscripcionVinculada = m.idSuscripcion || (m.bEnSuscripcionActiva ? infoSuscripcion?.idSuscripcion : null);
                                                const tieneSuscripcionActiva = !!idSuscripcionVinculada && (m.bEnSuscripcionActiva === true || (m.bEnSuscripcionActiva === undefined && m.idSuscripcion > 0));

                                                return (
                                                    <tr key={idModulo}>
                                                        <td className="text-dark fw-semibold">{idModulo}</td>
                                                        <td className="text-dark">{m.vClaveModulo || '—'}</td>
                                                        <td>
                                                            <Badge bg={isActive ? 'success' : 'secondary'}>
                                                                {isActive ? 'Activo' : 'Inactivo'}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-dark">
                                                            {tieneSuscripcionActiva ? (
                                                                <Link to={`/suscripciones/${idSuscripcionVinculada}`} className="text-decoration-none text-primary fw-semibold">
                                                                    Suscripción #{idSuscripcionVinculada}
                                                                </Link>
                                                            ) : (
                                                                'Sin cobro recurrente'
                                                            )}
                                                        </td>
                                                        <td className="text-dark">
                                                            {m.dFechaInicio ? formatearFechaAsignacion(m.dFechaInicio) : '—'}
                                                        </td>
                                                        <td className="text-dark">
                                                            {m.dFechaFin ? formatearFechaAsignacion(m.dFechaFin) : '—'}
                                                        </td>
                                                        <td className="text-dark">
                                                            {m.vCreatedUser}
                                                            {m.dCreatedDate
                                                                ? ` · ${formatearFechaAsignacion(m.dCreatedDate)}`
                                                                : ''}
                                                        </td>
                                                        <td className="text-end">
                                                            {isActive && tieneSuscripcionActiva && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    className="py-0 px-2"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                    title="Remover Módulo de Suscripción"
                                                                    onClick={() => handleAbrirModalRemover(m)}
                                                                >
                                                                    <FontAwesomeIcon icon={icon({ name: 'trash-can', style: 'solid' })} className="me-1" />
                                                                    Remover
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Vista Móvil (Tarjetas Compactas) */}
                            <div className="d-md-none p-2 bg-light">
                                {modulos.length === 0 ? (
                                    <div className="text-center text-muted py-3 small">
                                        Sin módulos asignados.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {modulos.map((m, idx) => {
                                            const isActive = getEstadoModulo(m);
                                            const idModulo = m.idModuloLicencia ?? m.idModulo ?? idx;
                                            const idSuscripcionVinculada = m.idSuscripcion || (m.bEnSuscripcionActiva ? infoSuscripcion?.idSuscripcion : null);
                                            const tieneSuscripcionActiva = !!idSuscripcionVinculada && (m.bEnSuscripcionActiva === true || (m.bEnSuscripcionActiva === undefined && m.idSuscripcion > 0));

                                            return (
                                                <div key={idModulo} className="border rounded-2 p-2 bg-white shadow-sm">
                                                    <div className="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom">
                                                        <div className="d-flex align-items-center gap-1">
                                                            <span className="text-muted fw-bold small">#{idModulo}</span>
                                                            <span className="fw-bold text-dark small">{m.vClaveModulo || 'Módulo'}</span>
                                                        </div>
                                                        <Badge bg={isActive ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                                                            {isActive ? 'Activo' : 'Inactivo'}
                                                        </Badge>
                                                    </div>

                                                    <div className="d-flex flex-column gap-1 text-secondary" style={{ fontSize: '0.78rem' }}>
                                                        <div>
                                                            <span className="fw-semibold text-dark me-1">Suscripción:</span>
                                                            {tieneSuscripcionActiva ? (
                                                                <Link to={`/suscripciones/${idSuscripcionVinculada}`} className="text-decoration-none text-primary fw-semibold">
                                                                    Suscripción #{idSuscripcionVinculada}
                                                                </Link>
                                                            ) : (
                                                                <span>Sin cobro recurrente</span>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <span className="fw-semibold text-dark me-1">Vigencia:</span>
                                                            {m.dFechaInicio ? formatearFechaAsignacion(m.dFechaInicio) : '—'}
                                                            {' al '}
                                                            {m.dFechaFin ? formatearFechaAsignacion(m.dFechaFin) : '—'}
                                                        </div>

                                                        {m.vCreatedUser && (
                                                            <div className="text-truncate">
                                                                <span className="fw-semibold text-dark me-1">Asignado por:</span>
                                                                {m.vCreatedUser}
                                                                {m.dCreatedDate ? ` · ${formatearFechaAsignacion(m.dCreatedDate)}` : ''}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {isActive && tieneSuscripcionActiva && (
                                                        <div className="text-end pt-1 border-top mt-1">
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="py-0 px-2"
                                                                style={{ fontSize: '0.72rem' }}
                                                                onClick={() => handleAbrirModalRemover(m)}
                                                            >
                                                                <FontAwesomeIcon icon={icon({ name: 'trash-can', style: 'solid' })} className="me-1" />
                                                                Remover
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            <ModalAgregarProductoSuscripcion
                show={showModal}
                onHide={() => setShowModal(false)}
                suscripcion={infoSuscripcion ? { ...infoSuscripcion, idLicencia: infoSuscripcion.idLicencia ?? idLicencia } : null}
                onCreated={cargarModulos}
            />

            {/* Modal de confirmación para remover módulo */}
            <Modal show={!!moduloARemover} onHide={() => !remiendo && setModuloARemover(null)} centered>
                <Modal.Header closeButton={!remiendo}>
                    <Modal.Title className="h6 text-danger">
                        <FontAwesomeIcon icon={icon({ name: 'triangle-exclamation', style: 'solid' })} className="me-2" />
                        Remover Módulo de Suscripción
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="small">
                    <p className="mb-2">
                        ¿Estás seguro de remover el módulo <strong>"{moduloARemover?.vDescripcionModulo || moduloARemover?.vNombreProducto || 'Módulo'}"</strong> de la suscripción?
                    </p>
                    <div className="alert alert-warning mb-3 p-2 small">
                        <strong>Atención:</strong> El módulo será desactivado en la licencia y se actualizará el monto recurrente de la suscripción en Stripe para no ser cobrado en futuras renovaciones.
                    </div>

                    <Form.Group className="mb-0">
                        <Form.Label className="fw-bold text-dark mb-1">Motivo de cancelación / remoción:</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Ej. Solicitado por el cliente, ya no requiere el módulo..."
                            value={vMotivoRemocion}
                            onChange={e => setVMotivoRemocion(e.target.value)}
                            disabled={remiendo}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setModuloARemover(null)} disabled={remiendo}>
                        Cancelar
                    </Button>
                    <Button variant="danger" size="sm" onClick={handleConfirmarRemover} disabled={remiendo}>
                        {remiendo ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Removiendo...
                            </>
                        ) : (
                            'Sí, Remover Módulo'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

SeccionModulosLicenciaCliente.propTypes = {
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    idClienteAkasia: PropTypes.number,
    infoSuscripcion: PropTypes.object
};

export default SeccionModulosLicenciaCliente;


